import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ValueDiff from '@/components/ValueDiff/ValueDiff';

const TimelineDetailsModal = ({ open, onClose, item, config = {} }) => {
  if (!item) {
    return null;
  }

  const {
    getTitle = () => 'Details',
    getIcon = () => ({ component: null, color: 'primary' }),
    getModalSections = () => [],
    formatDate = (date) => new Date(date).toLocaleString(),
    showValueDiff = true,
  } = config;

  const { component: iconComponent, color } = getIcon(item);
  const title = getTitle(item);
  const sections = getModalSections(item);

  const DefaultChangeInfoSection = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
        Change Information
      </Typography>
      <Box sx={{ pl: 2 }}>
        {item.changed_at && (
          <Typography variant='body2' sx={{ mb: 1 }}>
            <strong>Date:</strong> {formatDate(item.changed_at)}
          </Typography>
        )}
        {item.changed_by && (
          <Typography variant='body2' sx={{ mb: 1 }}>
            <strong>Changed by:</strong>{' '}
            {item.changed_by?.name ||
              item.changed_by?.email ||
              `User ${item.changed_by}`}
          </Typography>
        )}
        {item.id && (
          <Typography variant='body2'>
            <strong>ID:</strong> {item.id}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const DefaultValueDiffSection = () => {
    if (!showValueDiff || (!item.previous_value && !item.new_value)) {
      return null;
    }

    return (
      <ValueDiff
        previousValue={item.previous_value}
        newValue={item.new_value}
        title='Value Changes'
        maxHeight='300px'
      />
    );
  };

  const DefaultMetadataSection = () => {
    if (!item.metadata || Object.keys(item.metadata).length === 0) {
      return null;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
          Additional Information
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.50',
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
          }}>
          <pre
            style={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {JSON.stringify(item.metadata, null, 2)}
          </pre>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {iconComponent && (
            <Box sx={{ color: `${color}.main` }}>{iconComponent}</Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant='h6' component='div'>
              {title}
            </Typography>
            {item.action && (
              <Typography variant='body2' color='text.secondary'>
                Action: {item.action}
              </Typography>
            )}
          </Box>
          {item.action && (
            <Chip label={item.action} variant='outlined' size='small' />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {sections.map((section, index) => (
          <Box key={index}>
            {section.title && (
              <Typography
                variant='subtitle2'
                sx={{ mb: 1, fontWeight: 'bold' }}>
                {section.title}
              </Typography>
            )}
            {section.render(item)}
            {index < sections.length - 1 && <Divider sx={{ my: 2 }} />}
          </Box>
        ))}

        {sections.length === 0 && (
          <>
            <DefaultChangeInfoSection />
            <Divider sx={{ my: 2 }} />
            <DefaultValueDiffSection />
            <DefaultMetadataSection />
          </>
        )}

        {sections.length === 0 &&
          !item.changed_at &&
          !item.changed_by &&
          !item.previous_value &&
          !item.new_value &&
          (!item.metadata || Object.keys(item.metadata).length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant='body2' color='text.secondary'>
                No additional details available for this item.
              </Typography>
            </Box>
          )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimelineDetailsModal;
